/**
 * Recipients as a row of chips, not a comma-separated string and not a "who
 * gets this" dropdown. Type an address and press Enter (or comma, or paste a
 * list); click a chip to edit it; the X removes it. The delivery mode follows
 * the list: no addresses is retain-only, one is a reviewer copy, more is a team
 * distribution.
 */
import { useRef, useState } from 'react'
import { X } from 'lucide-react'
import { FOCUS } from './ui'
import clsx from 'clsx'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function RecipientsEditor({
  value,
  onChange,
  senderEmail,
  id = 'schedule-recipients',
}: {
  value: string[]
  onChange: (next: string[]) => void
  senderEmail: string | null
  id?: string
}) {
  const [draft, setDraft] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  function parse(raw: string): { emails: string[]; bad: string | null } {
    const parts = raw
      .split(/[;,\s]+/)
      .map((part) => part.trim().toLowerCase())
      .filter(Boolean)
    const bad = parts.find((part) => !EMAIL_RE.test(part)) ?? null
    return { emails: bad ? [] : parts, bad }
  }

  /** Fold the draft into the list; false when the draft is not an address. */
  function commit(raw = draft): boolean {
    const { emails, bad } = parse(raw)
    if (bad) {
      setProblem(`“${bad}” is not an email address.`)
      return false
    }
    if (emails.length) onChange([...value, ...emails.filter((email) => !value.includes(email))])
    setDraft('')
    setProblem(null)
    return true
  }

  function remove(address: string) {
    onChange(value.filter((item) => item !== address))
    setProblem(null)
  }

  /** Lift a chip back into the input so it can be corrected in place. */
  function edit(address: string) {
    const { emails, bad } = parse(draft)
    if (bad) {
      setProblem(`“${bad}” is not an email address.`)
      return
    }
    onChange([...value.filter((item) => item !== address), ...emails.filter((email) => !value.includes(email))])
    setDraft(address)
    setProblem(null)
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const sender = senderEmail || 'the workspace’s governed sender'
  const hint =
    value.length === 0
      ? 'No recipients: the report is generated and kept, and nothing is emailed.'
      : value.length === 1
        ? `Sent from ${sender} as a reviewer copy. Add a second address for a team distribution.`
        : `Sent from ${sender} to ${value.length} recipients as a team distribution.`

  return (
    <div>
      <label htmlFor={id} className="mb-1.5 block text-[12px] font-medium text-[#A2A2A2]">
        Recipients
      </label>
      <div
        className="flex min-h-[38px] cursor-text flex-wrap items-center gap-1.5 rounded-lg border border-[#2A2A2A] bg-[#080808] px-2 py-1.5 focus-within:border-[#FFD86B]"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((address) => (
          <span
            key={address}
            className="inline-flex items-center gap-0.5 rounded-full border border-[#4A3F1E] bg-[#171408] py-0.5 pl-2.5 pr-1 text-[12px] text-[#FFE9A8]"
          >
            <button
              type="button"
              title="Edit"
              onClick={(event) => {
                event.stopPropagation()
                edit(address)
              }}
              className={clsx('rounded hover:text-white', FOCUS)}
            >
              {address}
            </button>
            <button
              type="button"
              aria-label={`Remove ${address}`}
              onClick={(event) => {
                event.stopPropagation()
                remove(address)
              }}
              className={clsx('rounded-full p-0.5 text-[#A2A2A2] hover:bg-[#2A2418] hover:text-white', FOCUS)}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          autoComplete="off"
          value={draft}
          placeholder={value.length ? 'Add another…' : 'name@company.com, then Enter'}
          onChange={(event) => {
            setDraft(event.target.value)
            if (problem) setProblem(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ',' || event.key === ';') {
              event.preventDefault()
              commit()
            } else if (event.key === 'Backspace' && !draft && value.length) {
              event.preventDefault()
              edit(value[value.length - 1])
            }
          }}
          onBlur={() => {
            if (draft.trim()) commit()
          }}
          onPaste={(event) => {
            const text = event.clipboardData.getData('text')
            if (/[;,\s]/.test(text.trim())) {
              event.preventDefault()
              commit(`${draft} ${text}`)
            }
          }}
          className="min-w-[160px] flex-1 bg-transparent text-[13px] text-[#ECEAE4] placeholder:text-[#808080] focus:outline-none"
        />
      </div>
      {problem ? (
        <p role="alert" className="mt-1 text-[11.5px] text-[#F0B08A]">
          {problem}
        </p>
      ) : null}
      <p className="mt-1 text-[11.5px] text-[#8C8C8C]">{hint}</p>
    </div>
  )
}
